import { useState, useEffect, useCallback } from 'react';

export const SORT_KEY = 'ct:sort';
export const SORT_SECTIONS = Object.freeze(['whales', 'liqs', 'orders']);
export const SORT_MODES = Object.freeze(['time', 'size']);

export const DEFAULT_SORTS = Object.freeze({
  whales: 'time',
  liqs: 'time',
  orders: 'time',
});

function isValidMode(m) {
  return m === 'time' || m === 'size';
}

function getStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {}
  try {
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) return globalThis.localStorage;
  } catch {}
  return null;
}

export function loadSorts() {
  const storage = getStorage();
  if (!storage) return { ...DEFAULT_SORTS };
  try {
    const raw = storage.getItem(SORT_KEY);
    if (!raw) return { ...DEFAULT_SORTS };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_SORTS };
    return {
      whales: isValidMode(parsed.whales) ? parsed.whales : DEFAULT_SORTS.whales,
      liqs: isValidMode(parsed.liqs) ? parsed.liqs : DEFAULT_SORTS.liqs,
      orders: isValidMode(parsed.orders) ? parsed.orders : DEFAULT_SORTS.orders,
    };
  } catch {
    return { ...DEFAULT_SORTS };
  }
}

export function saveSorts(state) {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(SORT_KEY, JSON.stringify(state));
  } catch {}
}

export function applySort(state, section, mode) {
  if (!SORT_SECTIONS.includes(section)) return state;
  const next = isValidMode(mode) ? mode : DEFAULT_SORTS[section];
  return { ...state, [section]: next };
}

export function toggleSort(state, section) {
  if (!SORT_SECTIONS.includes(section)) return state;
  return { ...state, [section]: state[section] === 'size' ? 'time' : 'size' };
}

function toMs(ts) {
  if (ts == null) return 0;
  if (typeof ts === 'number') return Number.isFinite(ts) ? ts : 0;
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function sortWhales(rows, mode) {
  const list = Array.isArray(rows) ? rows : [];
  if (mode === 'size') {
    return [...list].sort((a, b) => (Number(b?.dollarSize) || 0) - (Number(a?.dollarSize) || 0));
  }
  return [...list].sort((a, b) => toMs(b?.timestamp) - toMs(a?.timestamp));
}

export function sortLiqs(rows, mode) {
  return sortWhales(rows, mode);
}

export function sortOrderWhales(rows, mode) {
  const list = Array.isArray(rows) ? rows : [];
  if (mode === 'size') {
    return [...list].sort((a, b) => (Number(b?.size) || 0) - (Number(a?.size) || 0));
  }
  return [...list].sort((a, b) => {
    if (a?.side === b?.side) return (Number(b?.price) || 0) - (Number(a?.price) || 0);
    return a?.side === 'ask' ? -1 : 1;
  });
}

export function useSortModes() {
  const [sorts, setSorts] = useState(() => loadSorts());

  useEffect(() => {
    saveSorts(sorts);
  }, [sorts]);

  const setMode = useCallback((section, mode) => {
    setSorts(prev => applySort(prev, section, mode));
  }, []);

  const toggle = useCallback((section) => {
    setSorts(prev => toggleSort(prev, section));
  }, []);

  return { sorts, setMode, toggle };
}
